import type { ImageProps } from "next/image";
import Image from "next/image";

import type { EventName } from "@mw/shared/analyticsEventNames";

import { BaseLink } from "@components/core/Link";
import { BaseLinkTracked } from "@components/core/LinkTracked";
import { Noise } from "@ui/components/core/Noise/Noise";
import { SeamlessTexture } from "@ui/components/core/SeamlessTexture/SeamlessTexture";
import { cn } from "@ui/lib/utils";
import { sizes } from "@ui/lib/sizes";

export interface CategoryGridItem {
  id?: string | null;
  imageProps?: ImageProps | null;
  title: string;
  description?: string | null;
  href?: string | null;
  eventName?: EventName;
  eventProperties?: Record<string, unknown>;
}

export interface CategoryGridProps {
  items: CategoryGridItem[];
}

function CategoryGridCard({ item }: { item: CategoryGridItem }) {
  return (
    <div className='tw-relative tw-group tw-p-4 tw-pb-6'>
      {item.href &&
        (item.eventName ? (
          <BaseLinkTracked
            href={item.href}
            aria-label={item.title}
            className='tw-absolute tw-inset-0 tw-z-10'
            eventName={item.eventName}
            eventProperties={item.eventProperties}
          />
        ) : (
          <BaseLink href={item.href} aria-label={item.title} className='tw-absolute tw-inset-0 tw-z-10' />
        ))}
      <div className='tw-relative tw-aspect-square tw-overflow-hidden tw-rounded-sm'>
        {item.imageProps && (
          <Image
            {...item.imageProps}
            fill={true}
            alt={item.imageProps.alt}
            className='tw-object-cover'
            sizes={sizes("100vw", { md: "50vw", lg: "25vw" })}
          />
        )}
        <SeamlessTexture className='tw-pointer-events-none' aria-hidden='true' />
        <Noise className='tw-pointer-events-none tw-opacity-60' aria-hidden='true' />
      </div>
      <p className='tw-mt-4 tw-type-lg tw-text-black'>{item.title}</p>
      {item.description && <p className='tw-mt-2 tw-type-sm tw-text-gray-500'>{item.description}</p>}
    </div>
  );
}

export function CategoryGrid({ items }: CategoryGridProps) {
  if (!items || items.length === 0) return null;

  const lastRowStart2 = items.length - (items.length % 2 || 2);
  const lastRowStart4 = items.length - (items.length % 4 || 4);

  return (
    <div className='tw-grid tw-grid-cols-12'>
      {items.map((item, index) => {
        const pos = index + 1;
        const isLast = index === items.length - 1;
        const isLastCol2 = pos % 2 === 0;
        const isLastCol4 = pos % 4 === 0;

        const rightBorder = isLast || isLastCol4 ? undefined : isLastCol2 ? "lg:tw-border-r" : "md:tw-border-r";

        return (
          <div
            key={item.id ?? item.title}
            className={cn(
              "tw-col-span-12 md:tw-col-span-6 lg:tw-col-span-3",
              "tw-border-black/5",
              "tw-border-b",
              isLast && "tw-border-b-0",
              index >= lastRowStart2 && "md:tw-border-b-0",
              index >= lastRowStart4 && "lg:tw-border-b-0",
              rightBorder,
            )}
          >
            <CategoryGridCard item={item} />
          </div>
        );
      })}
    </div>
  );
}
